import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Dropdown, Spinner, Alert } from 'react-bootstrap';
import { Link, useRouteMatch, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMusic,
  faPlay,
  faTrash,
  faEdit,
  faDownload,
  faShare,
  faEllipsisV,
  faFolderOpen,
  faArrowLeft,
  faFilter,
  faSort,
  faUser,
  faCalendar,
  faFileAudio
} from "@fortawesome/free-solid-svg-icons";
import { getProjects, deleteProject } from '../../../service/beats-api';
import { useAudio } from "../context/AudioContext";
import '../components/components.css';

const MyLibrary = () => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const [filterBy, setFilterBy] = useState('all');
  const [deletingProject, setDeletingProject] = useState(null);
  const { playAudio } = useAudio();

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [projects, sortBy, filterBy]);

  const loadProjects = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getProjects();
      if (response.success) {
        setProjects(response.projects);
      } else {
        setError('Failed to load projects');
      }
    } catch (err) {
      console.error('Error loading projects:', err);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...projects];

    // Apply filter
    if (filterBy !== 'all') {
      filtered = filtered.filter(project => project.status === filterBy.toUpperCase());
    }

    // Apply sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'created':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'modified':
        case 'recent':
        default:
          return new Date(b.modified_at) - new Date(a.modified_at);
      }
    });

    setFilteredProjects(filtered);
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Are you sure you want to delete "${projectName}"? This action cannot be undone.`)) {
      return;
    }

    setDeletingProject(projectId);
    try {
      const response = await deleteProject(projectId);
      if (response.success) {
        // Remove project from local state
        setProjects(prev => prev.filter(p => p.id !== projectId));
      } else {
        alert('Failed to delete project. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting project:', err);
      alert('Failed to delete project. Please try again.');
    } finally {
      setDeletingProject(null);
    }
  };

  const handlePlayFirstAsset = (project) => {
    if (project.audio_assets && project.audio_assets.length > 0) {
      const firstAsset = project.audio_assets[0];
      if (firstAsset.download_url && firstAsset.status === 'READY') {
        playAudio(firstAsset.download_url, firstAsset.file_name);
      }
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'COMPLETED': 'success',
      'IN_PROGRESS': 'warning', 
      'DRAFT': 'secondary',
      'ARCHIVED': 'dark'
    };
    const labels = {
      'COMPLETED': 'Completed',
      'IN_PROGRESS': 'In Progress',
      'DRAFT': 'Draft',
      'ARCHIVED': 'Archived'
    };
    return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'Unknown';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getTotalDuration = (project) => {
    if (!project.audio_assets || project.audio_assets.length === 0) return 'No assets';
    const totalSeconds = project.audio_assets.reduce((sum, asset) => sum + (asset.duration || 0), 0);
    return formatDuration(totalSeconds);
  };

  const getFilterLabel = (filter) => {
    const labels = {
      'all': 'All Projects',
      'completed': 'Completed',
      'in_progress': 'In Progress',
      'draft': 'Drafts',
      'archived': 'Archived'
    };
    return labels[filter] || filter;
  };

  const getSortLabel = (sort) => {
    const labels = {
      'recent': 'Most Recent',
      'modified': 'Last Modified',
      'created': 'Date Created',
      'name': 'Name A-Z',
      'status': 'Status'
    };
    return labels[sort] || sort;
  };

  if (loading) {
    return (
      <Container fluid className="py-4 beats-component-container">
        <Row className="justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Col md={6} className="text-center">
            <Spinner animation="border" className="mb-3" style={{ color: 'var(--purple-accent)' }} />
            <h4 className="mb-3" style={{ color: '#d9d9d9' }}>Loading Your Library...</h4>
            <p className="text-muted">Fetching your projects</p>
          </Col>
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="py-4 beats-component-container">
        <Row className="justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <Col md={6} className="text-center">
            <Alert variant="warning" className="mb-4">
              <FontAwesomeIcon icon={faMusic} className="mb-3" style={{ fontSize: 'var(--font-5xl)' }} />
              <h4 className="mb-3" style={{ color: '#d9d9d9' }}>Failed to Load Library</h4>
              <p style={{ color: '#d9d9d9' }}>{error}</p>
            </Alert>
            <div className="d-flex gap-2 justify-content-center">
              <Button 
                as={Link}
                to={url.replace('/my-library', '')}
                className="beats-btn-purple"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Home
              </Button>
              <Button 
                variant="outline-light"
                onClick={loadProjects}
                className="beats-btn-outline"
              >
                Try Again
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 beats-component-container">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <Link to={url.replace('/my-library', '')} className="beats-btn-outline me-3">
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Home
              </Link>
              <div>
                <h2 className="beats-heading mb-1" style={{ color: '#d9d9d9' }}>My Library</h2>
                <p className="text-muted mb-0">
                  {filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'}
                  {filterBy !== 'all' && ` (filtered from ${projects.length} total)`}
                </p>
              </div>
            </div>
            <div className="d-flex gap-2">
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  <FontAwesomeIcon icon={faFilter} className="me-2" />
                  {getFilterLabel(filterBy)}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setFilterBy('all')}>All Projects</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterBy('completed')}>Completed</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterBy('in_progress')}>In Progress</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterBy('draft')}>Drafts</Dropdown.Item>
                  <Dropdown.Item onClick={() => setFilterBy('archived')}>Archived</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  <FontAwesomeIcon icon={faSort} className="me-2" />
                  {getSortLabel(sortBy)}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => setSortBy('recent')}>Most Recent</Dropdown.Item>
                  <Dropdown.Item onClick={() => setSortBy('modified')}>Last Modified</Dropdown.Item>
                  <Dropdown.Item onClick={() => setSortBy('created')}>Date Created</Dropdown.Item>
                  <Dropdown.Item onClick={() => setSortBy('name')}>Name A-Z</Dropdown.Item>
                  <Dropdown.Item onClick={() => setSortBy('status')}>Status</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Col>
      </Row>

      {/* Projects Grid */}
      {filteredProjects.length > 0 ? (
        <Row xs={1} md={2} lg={3} className="g-4">
          {filteredProjects.map((project) => (
            <Col key={project.id}>
              <Card className="beats-card h-100 project-card">
                <Card.Body className="d-flex flex-column">
                  {/* Header */}
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="flex-grow-1">
                      <h5 className="mb-1 text-truncate" style={{ color: '#d9d9d9' }}>{project.name}</h5>
                      <p className="text-muted small mb-2">
                        <FontAwesomeIcon icon={faUser} className="me-1" />
                        {project.owner_username} • {project.asset_count} assets
                      </p>
                      {/* {getStatusBadge(project.status)} */}
                    </div>
                    <Dropdown>
                      <Dropdown.Toggle variant="link" className="text-muted p-0 border-0 bg-transparent">
                        <FontAwesomeIcon icon={faEllipsisV} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu align="end">
                        <Dropdown.Item onClick={() => history.push(`${url.replace('/my-library', '')}/project/${project.id}`)}>
                          <FontAwesomeIcon icon={faEdit} className="me-2" />
                          Open Project
                        </Dropdown.Item>
                        <Dropdown.Item disabled>
                          <FontAwesomeIcon icon={faDownload} className="me-2" />
                          Download
                        </Dropdown.Item>
                        <Dropdown.Item disabled>
                          <FontAwesomeIcon icon={faShare} className="me-2" />
                          Share
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item 
                          className="text-danger"
                          onClick={() => handleDeleteProject(project.id, project.name)}
                          disabled={deletingProject === project.id}
                        >
                          <FontAwesomeIcon icon={faTrash} className="me-2" />
                          {deletingProject === project.id ? 'Deleting...' : 'Delete'}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  {/* Project Details */}
                  <div className="mb-3 flex-grow-1">
                    <div className="d-flex justify-content-between text-small mb-2">
                      <span>
                        <FontAwesomeIcon icon={faCalendar} className="me-1" />
                        {new Date(project.modified_at).toLocaleDateString()}
                      </span>
                      <span>
                        <FontAwesomeIcon icon={faFileAudio} className="me-1" />
                        {getTotalDuration(project)}
                      </span>
                    </div>
                    
                    {/* Description */}
                    {project.description && (
                      <div className="mb-2">
                        <small className="text-muted d-block mb-1">Description:</small>
                        <p className="small mb-0" style={{ color: '#d9d9d9' }}>
                          {project.description.length > 100 
                            ? `${project.description.substring(0, 100)}...` 
                            : project.description
                          }
                        </p>
                      </div>
                    )}

                    {/* Audio Assets Preview */}
                    {project.audio_assets && project.audio_assets.length > 0 && (
                      <div className="mb-2">
                        <small className="text-muted d-block mb-1">Latest Assets:</small>
                        <div className="d-flex flex-wrap gap-1">
                          {project.audio_assets.slice(0, 3).map((asset, index) => (
                            <Badge key={index} bg="dark" className="asset-badge small">
                              {asset.type}
                            </Badge>
                          ))}
                          {project.audio_assets.length > 3 && (
                            <Badge bg="secondary" className="small">
                              +{project.audio_assets.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Tags */}
                    {project.tags && project.tags.length > 0 && (
                      <div>
                        <small className="text-muted d-block mb-1">Tags:</small>
                        <div className="d-flex flex-wrap gap-1">
                          {project.tags.slice(0, 3).map((tag, index) => (
                            <span 
                              key={index}
                              className="badge me-1"
                              style={{ fontSize: 'var(--font-sm)', fontWeight: '400', backgroundColor: '#2589BD', color: 'white', opacity: 0.8 }}
                            >
                              #{tag}
                            </span>
                          ))}
                          {project.tags.length > 3 && (
                            <Badge bg="secondary" className="small">
                              +{project.tags.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="d-flex gap-2 mt-auto">
                    <Button 
                      as={Link}
                      to={`${url.replace('/my-library', '')}/project/${project.id}`}
                      className="beats-btn-purple flex-grow-1"
                    >
                      <FontAwesomeIcon icon={faFolderOpen} className="me-2" />
                      Open Project
                    </Button>
                    <Button 
                      variant="outline-light" 
                      size="sm"
                      className="beats-btn-outline"
                      onClick={() => handlePlayFirstAsset(project)}
                      disabled={!project.audio_assets || project.audio_assets.length === 0}
                      title="Play first audio asset"
                    >
                      <FontAwesomeIcon icon={faPlay} />
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        /* Empty State */
        <Row className="justify-content-center mt-5">
          <Col md={6} className="text-center">
            <FontAwesomeIcon 
              icon={faMusic} 
              className="mb-4" 
              style={{ fontSize: 'var(--font-5xl)', color: 'var(--text-muted)' }} 
            />
            <h4 className="mb-3" style={{ color: '#d9d9d9' }}>
              {filterBy === 'all' ? 'No projects yet' : `No ${getFilterLabel(filterBy).toLowerCase()}`}
            </h4>
            <p className="text-muted mb-4">
              {filterBy === 'all' 
                ? 'Start creating your first project to see it here'
                : 'Try changing your filter to see more projects'
              }
            </p>
            <div className="d-flex gap-2 justify-content-center">
              <Button 
                as={Link}
                to={url.replace('/my-library', '')}
                className="beats-btn-purple"
              >
                Create Your First Project
              </Button>
              {filterBy !== 'all' && (
                <Button 
                  variant="outline-light"
                  onClick={() => setFilterBy('all')}
                  className="beats-btn-outline"
                >
                  Show All Projects
                </Button>
              )}
            </div>
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default MyLibrary; 